import { Component, DetailEvent, Entity, registerComponent, registerSystem, System } from "aframe";
import { Object3D } from "three";
import { Guid, parseGuid } from "../rtc/guid";

export type Variable = number | string | Guid | boolean | null;

export type Variables = { [name:string] : Variable };
export type VariablesChangedEvent = DetailEvent<Variables>;

export interface VariablesComponent extends Component { 
	el : VariablesEntity;
	data : Variables; 
	evaluate() : Variables;
}
export type VariablesEntity = Entity< { [attr:string] : Component } & { "variables" : VariablesComponent } > & 
{ 
	variables : Variables 
};


const propagateChange = (el : Entity, obj : Object3D & { el ?: VariablesEntity }, changed : Variables ) =>
{
	if(obj.el && (obj.el !== el))
	{
		el = obj.el; //new scope
		if(el.dataset.inheritedVariablesListeners)
			el.emit("inherited-variables-changed", changed, false );
	}
	for(let child of obj.children)
		propagateChange(el, child, changed );
}

export function propagateVariablesChange(el : Entity)
{
	propagateChange(el, el.object3D, {});
}


interface VariableInheritanceSystem extends System
{
	data : { [variable:string] : string };
}

registerSystem("variable-inheritance", {
	schema : { "" : { type: "string" } } //variable_name : inheritance_rule
});



const  inheritanceRules : { [variable:string] : (current : any, parent : any) => any } = 
{
	"inherit" : (current, parent) => parent,
	"parent" : (current, parent) => parent,
	"none" : (current, parent) => current,
	"min" : (current, parent) => (current === undefined) ? parent : ((current < parent) ? current : parent),
	"max" : (current, parent) => (current === undefined) ? parent : ((current > parent) ? current : parent),
	"and" : (current, parent) => (current === undefined) ? parent : (current && parent),
	"or" : (current, parent) => current || parent,
};



const parseVariable = (value : any) =>
{
	if(value === undefined) return null;
	if(typeof value != "string")
	{
		if(value instanceof String) return value.valueOf();
		if(value instanceof Boolean) return value.valueOf();
		if(value instanceof Number) return value.valueOf();
		if(value instanceof Element) return value.id || String(value);
		return value; //Whatever this is
	} 
	if(value.startsWith("#")) return parseGuid(value.slice(1));
	if(value.startsWith("0x")) return parseInt(value.slice(2));
	if(value.startsWith("\'") && value.endsWith("\'")) return value.slice(1, value.length - 1);
	try { return JSON.parse(value); } //parses null, true, false, numbers, double-quoted strings
	catch(err) { return String(value); }
};


registerComponent("variables", {
	schema : { "" : { 
		default : 0,
		parse : parseVariable,
		stringify : (value : Variable) =>
		{
			if((typeof value == "object") && (value !== null))
			{
				if(value instanceof Guid) return "#" + value.toString();
				return String(value); //Fingers crossed
			}
			return JSON.stringify(value);
		}
	} },
	init(this : VariablesComponent)
	{
		this.el.addEventListener("set-variables", event =>
		{
			const detail = (event as DetailEvent<Variables>).detail;

			const newValues : Variables = {};
			for(let key in detail)
			{
				const value = detail[key];
				if(value === undefined)
					this.el.removeAttribute("variables", key);
				else
					newValues[key] = value;
			}
			for(let anyKey in newValues)
			{
				this.el.setAttribute("variables", newValues);
				break;
			}
			event.stopPropagation();
			event.preventDefault();
		});
	},
	update(this : VariablesComponent, oldData : Variables )
	{
		//Shortcut : add this.data as this.el.variables (shorter than el.components.variables.data)
		this.el.variables = this.data;
		
		const changed : Variables = { };
		for(let key in this.data)
			if(oldData[key] != this.data[key])
				changed[key] = this.data[key];
		
		for(let any in changed)
		{
			const el = this.el;
			el.emit("variables-changed", changed, false);
			propagateChange(el, el.object3D, changed);
			return;
		}
	},
	
	remove(this : VariablesComponent)
	{
		const el = this.el;
		if(!el.isConnected) return; //Being destroyed, no need to change
		el.emit("variables-changed", this.data, false);
		propagateChange(el, el.object3D, this.data);
	},

	evaluate(this : VariablesComponent)
	{
		const result : Variables = {};
		if(this.data)
			for(let key in this.data)
				result[key] = this.data[key];

		const inhSys = this.el.sceneEl!.systems["variable-inheritance"] as VariableInheritanceSystem;

		//Inherit variables from 3D scene instead of parentEl pseudo-hierarchy :
		let currentEl = this.el;
		for(let pObj : Object3D & { el? : Entity } = this.el.object3D; pObj = pObj.parent!; )
		{
			if(!pObj.el) continue;
			if(pObj.el === currentEl) continue;
			
			currentEl = pObj.el as VariablesEntity;
			const pv = currentEl.components["variables"] as VariablesComponent;
			if(!(pv && pv.data))
				continue;

			for(let key in pv.data)
			{
				const ruleName = inhSys.data[ key ];
				const ruleFn = ruleName ? inheritanceRules[ ruleName ] : undefined;

				let value = pv.data[key];
				if(ruleFn)
					value = ruleFn( result[key], value );
				else if(key in result) //No rule = override : existing value prevails.
					continue;

				if(value === undefined)
					delete result[key];
				else
					result[key] = value;
			}

		}

		return result;
	}
});

